import { useState, useRef, useEffect, useCallback } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../../stores/authStore'
import { 
  Bars3Icon, 
  XMarkIcon, 
  UserCircleIcon,
  ChevronDownIcon,
  ArrowRightOnRectangleIcon,
  UserIcon,
  AcademicCapIcon,
  BookOpenIcon,
  HomeIcon,
  UsersIcon
} from '@heroicons/react/24/outline'

const Header = () => {
  const { isAuthenticated, user, logout } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)
  const mobileMenuRef = useRef<HTMLDivElement>(null)

  // 首页特殊处理：顶部透明，滚动变白
  const isHomePage = location.pathname === '/'
  const headerClass = isHomePage && !scrolled 
    ? 'bg-transparent text-white border-transparent' 
    : 'bg-white/80 backdrop-blur-xl text-gray-900 shadow-lg shadow-gray-200/50 border-gray-100'

  const linkClass = isHomePage && !scrolled
    ? 'text-white/90 hover:text-white hover:bg-white/10'
    : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // 锁定背景滚动
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden'
      document.body.style.touchAction = 'none'
    } else {
      document.body.style.overflow = ''
      document.body.style.touchAction = ''
    }
    return () => {
      document.body.style.overflow = ''
      document.body.style.touchAction = ''
    }
  }, [isMobileMenuOpen])

  // 关闭移动端菜单
  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false)
  }, [])

  const handleLogout = () => {
    logout()
    navigate('/')
    setIsUserMenuOpen(false)
    setIsMobileMenuOpen(false)
  }

  // 点击外部关闭用户菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const getDashboardLink = () => {
    if (user?.role === 'teacher') return '/teacher/dashboard'
    if (user?.role === 'admin') return '/admin/dashboard'
    return '/student/dashboard'
  }

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b ${headerClass}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 md:h-20">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center cursor-pointer" onClick={() => navigate('/')}>
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center mr-3 transition-all duration-300 ${isHomePage && !scrolled ? 'bg-white/10 backdrop-blur-md border border-white/20' : 'bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg shadow-blue-500/25'}`}>
              <span className="text-2xl">🎓</span>
            </div>
            <span className={`text-2xl font-bold tracking-tight transition-colors duration-300 ${isHomePage && !scrolled ? 'text-white' : 'text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600'}`}>
              云庠教育
            </span>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-1">
            {[
              { name: '首页', path: '/' },
              { name: '浏览课程', path: '/courses' },
              { name: '寻找导师', path: '/teachers' },
            ].map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${linkClass}`}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Auth buttons / User Menu */}
          <div className="hidden md:flex items-center space-x-4">
            {isAuthenticated ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className={`flex items-center space-x-3 pl-2 pr-4 py-1.5 rounded-full transition-all border ${
                    isHomePage && !scrolled 
                      ? 'bg-white/10 border-white/20 text-white hover:bg-white/20' 
                      : 'bg-white border-gray-200 text-gray-700 hover:border-blue-300 hover:shadow-md'
                  }`}
                >
                  {user?.avatar ? (
                    <img 
                      src={user.avatar} 
                      alt={user.firstName || 'User'} 
                      className="h-8 w-8 rounded-full object-cover border-2 border-white"
                    />
                  ) : (
                    <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white shadow-sm">
                      {user?.firstName?.[0] || <UserCircleIcon className="h-5 w-5" />}
                    </div>
                  )}
                  <span className="font-medium text-sm max-w-[100px] truncate">{user?.firstName || '用户'}</span>
                  <ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ${isUserMenuOpen ? 'transform rotate-180' : ''}`} />
                </button>

                {/* Dropdown Menu */}
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-3 w-64 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 ring-1 ring-black ring-opacity-5 animate-scaleIn origin-top-right overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-50 bg-gray-50/50">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">当前登录</p>
                      <p className="text-sm font-bold text-gray-900 truncate">{user?.email}</p>
                      <span className="mt-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                        {user?.role}
                      </span>
                    </div>
                    
                    <div className="py-2">
                      <Link
                        to={getDashboardLink()}
                        className="flex items-center px-6 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center mr-3 text-blue-600">
                          <UserIcon className="h-5 w-5" />
                        </div>
                        <div>
                          <p className="font-medium">控制台</p>
                          <p className="text-xs text-gray-500 mt-0.5">管理您的课程与进度</p>
                        </div>
                      </Link>
                      
                      <Link
                        to="/profile"
                        className="flex items-center px-6 py-3 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-600 transition-colors"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center mr-3 text-purple-600">
                          <AcademicCapIcon className="h-5 w-5" />
                        </div>
                        <div>
                          <p className="font-medium">个人资料</p>
                          <p className="text-xs text-gray-500 mt-0.5">查看与修改信息</p>
                        </div>
                      </Link>
                    </div>
                    
                    <div className="border-t border-gray-100 mt-2 pt-2">
                      <button
                        onClick={handleLogout}
                        className="flex w-full items-center px-6 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <ArrowRightOnRectangleIcon className="h-5 w-5 mr-3" />
                        退出登录
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                <Link
                  to="/login"
                  className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
                    isHomePage && !scrolled 
                      ? 'text-white hover:bg-white/10' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  登录
                </Link>
                <Link
                  to="/register"
                  className={`px-6 py-2.5 rounded-full text-sm font-bold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 ${
                    isHomePage && !scrolled
                      ? 'bg-white text-blue-600 hover:bg-blue-50'
                      : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:shadow-blue-500/40'
                  }`}
                >
                  免费注册
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="flex md:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`p-2 rounded-lg transition-colors ${
                isHomePage && !scrolled ? 'text-white hover:bg-white/20' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <span className="sr-only">打开菜单</span>
              {isMobileMenuOpen ? (
                <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
              ) : (
                <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div 
        className={`md:hidden fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm transition-all duration-300 ease-out ${
          isMobileMenuOpen 
            ? 'opacity-100 visible' 
            : 'opacity-0 invisible pointer-events-none'
        }`} 
        onClick={closeMobileMenu}
        aria-hidden={!isMobileMenuOpen}
      />
      
      {/* Mobile Menu Sidebar */}
      <div 
        ref={mobileMenuRef}
        className={`md:hidden fixed top-0 right-0 bottom-0 w-[85vw] max-w-[320px] sm:max-w-[360px] bg-white z-[70] transform transition-transform duration-300 ease-out shadow-2xl flex flex-col ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ 
          paddingTop: 'env(safe-area-inset-top, 0px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          paddingRight: 'env(safe-area-inset-right, 0px)'
        }}
        role="dialog"
        aria-modal="true"
        aria-label="导航菜单"
      >
        {/* Header */}
        <div className="flex-shrink-0 p-5 sm:p-6 flex justify-between items-center border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center">
              <span className="text-xl">🎓</span>
            </div>
            <span className="text-lg font-bold text-gray-900">云庠教育</span>
          </div>
          <button 
            onClick={closeMobileMenu} 
            className="p-2.5 rounded-full hover:bg-gray-100 text-gray-500 active:scale-95 transition-all"
            aria-label="关闭菜单"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>
        
        {/* Navigation Links */}
        <div className="flex-1 overflow-y-auto py-4 px-3 sm:px-4 space-y-1 overscroll-contain">
          {[
            { name: '首页', path: '/', icon: HomeIcon },
            { name: '浏览课程', path: '/courses', icon: BookOpenIcon },
            { name: '寻找导师', path: '/teachers', icon: UsersIcon },
          ].map((item) => {
            const isActive = location.pathname === item.path
            const Icon = item.icon
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center p-3.5 sm:p-4 rounded-xl font-medium transition-all duration-200 ${
                  isActive 
                    ? 'bg-blue-50 text-blue-600 shadow-sm' 
                    : 'text-gray-700 hover:bg-gray-50 active:bg-gray-100'
                }`}
                onClick={closeMobileMenu}
              >
                <Icon className={`w-5 h-5 mr-3 ${isActive ? 'text-blue-500' : 'text-gray-400'}`} />
                {item.name}
                {isActive && (
                  <div className="ml-auto w-1.5 h-1.5 rounded-full bg-blue-500" />
                )}
              </Link>
            )
          })}
        </div>
        
        {/* Footer with Auth Actions */}
        <div 
          className="flex-shrink-0 p-4 sm:p-6 border-t border-gray-100 bg-gradient-to-t from-gray-50 to-white"
          style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 1rem)' }}
        >
          {isAuthenticated ? (
            <div className="space-y-4">
              {/* User Info */}
              <div className="flex items-center p-3 rounded-xl bg-gray-50">
                <div className="flex-shrink-0">
                  {user?.avatar ? (
                    <img className="h-12 w-12 rounded-full border-2 border-white shadow-sm object-cover" src={user.avatar} alt={user?.firstName || '用户头像'} />
                  ) : (
                    <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white shadow-sm">
                      {user?.firstName?.[0] || <UserCircleIcon className="h-7 w-7" />}
                    </div>
                  )}
                </div>
                <div className="ml-3 overflow-hidden flex-1">
                  <div className="text-sm font-bold text-gray-900 truncate">{user?.firstName} {user?.lastName}</div>
                  <div className="text-xs text-gray-500 truncate">{user?.email}</div>
                  <span className="mt-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 capitalize">
                    {user?.role === 'admin' ? '管理员' : user?.role === 'teacher' ? '教师' : '学生'}
                  </span>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-3">
                <Link
                  to={getDashboardLink()}
                  className="flex justify-center items-center px-4 py-3 rounded-xl bg-blue-600 text-white font-medium shadow-lg shadow-blue-500/25 hover:bg-blue-700 active:scale-[0.98] transition-all"
                  onClick={closeMobileMenu}
                >
                  控制台
                </Link>
                <button
                  onClick={handleLogout}
                  className="flex justify-center items-center px-4 py-3 rounded-xl bg-white border border-gray-200 text-gray-700 font-medium hover:bg-gray-50 active:scale-[0.98] transition-all"
                >
                  退出登录
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-gray-500 text-center mb-4">登录后可访问更多功能</p>
              <div className="grid grid-cols-2 gap-3">
                <Link
                  to="/login"
                  className="flex justify-center items-center px-4 py-3.5 border border-gray-200 rounded-xl text-gray-700 font-medium bg-white hover:bg-gray-50 active:scale-[0.98] transition-all"
                  onClick={closeMobileMenu}
                >
                  登录
                </Link>
                <Link
                  to="/register"
                  className="flex justify-center items-center px-4 py-3.5 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl text-white font-medium shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 active:scale-[0.98] transition-all"
                  onClick={closeMobileMenu}
                >
                  免费注册
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}

export default Header
